import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  defaultIfEmpty,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import {
  AdBreakPlatformSpec,
  AdBreakPlatformSpecService,
  jobData,
  JobItem,
  JobsApiService,
  LoggingInfo,
  LoggingService,
  modelContext,
  VideoAnalysisResultsResponse,
} from '@vdms-hq/api-contract';
import { ActiveAssetService } from '@vdms-hq/asset-details';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Timecode } from '@vdms-hq/timecode';
import { castTo, SelectOption } from '@vdms-hq/shared';
import { ToastService } from '@vdms-hq/toast';
import { v4 as uuidv4 } from 'uuid';
import { OmakaseMarkerTrackService, PlayerService } from '@vdms-hq/player';
import { map } from 'rxjs/operators';

export interface AdBreakFormArrayObject {
  timestamp: number;
  highlighted: boolean;
  selected: boolean;
  hint: string;
}

export interface AdBreakFormGroupObject {
  selectedTimestamp: string;
  isValid: boolean | null;
  adBreaks: AdBreakFormArrayObject[];
}

export interface AdBreakFormArray {
  timestamp: FormControl<number | null>;
  highlighted: FormControl<boolean>;
  selected: FormControl<boolean>;
  hint: FormControl<string>;
}

export interface AdBreakFormGroup {
  selectedTimestamp: FormControl<string | null>;
  isValid: FormControl<boolean | null>;
  adBreaks: FormArray<FormGroup<AdBreakFormArray>>;
}

interface AdBreakMarkerValue {
  id: string | number;
  tcInSeconds: number;
  tcOutSeconds: number | undefined;
  tooltip: string;
  color: string;
}

@Injectable({ providedIn: 'root' })
export class AdBreakService {
  readonly DEFAULT_OPTIONS_PER_BREAK = 3;
  readonly MIN_OPTIONS_PER_BREAK = 1;
  readonly MAX_OPTIONS_PER_BREAK = 6;
  readonly N_A = 'N/A';
  readonly DEFAULT_TIMECODE = '00:00:00:00';

  #activeAssetService = inject(ActiveAssetService);
  #jobsApiService = inject(JobsApiService);
  #toast: ToastService = inject(ToastService);
  #loggingApi: LoggingService = inject(LoggingService);
  #markerTrackService = inject(OmakaseMarkerTrackService);
  #platformSpecService = inject(AdBreakPlatformSpecService);
  #playerService!: PlayerService;

  markerTrackData: { id: string; values: AdBreakMarkerValue[] } = {
    id: 'adBreaksTrack',
    values: [],
  };

  #destroyed$ = new Subject<void>();
  #asset$ = this.#activeAssetService.currentAssetView$;
  #jobsItems$ = this.#asset$.pipe(
    filter(Boolean),
    switchMap((asset) => {
      this.durationSec = asset.file_metadata.duration_sec;
      this.currentAssetUuid = asset.uuid;
      this.currentAssetOffset = asset.currentOffset?.toString() ?? this.DEFAULT_TIMECODE;

      return this.#jobsApiService.getAssetJobs(asset.uuid);
    }),
    shareReplay(1),
  );
  /**
   * Credits and black frames come from technical cue detection since segment detection was retired (#16419),
   * falling back to segment detection so assets analysed before the retirement keep working. Emits an empty
   * list when the asset has neither, so the fallbacks below still run.
   */
  #technicalCueItems$: Observable<JobItem[]> = this.#jobsItems$.pipe(
    switchMap((jobsResponse) => {
      const url = this.#latestResultUrl(jobsResponse.jobs, [
        modelContext.VIDEO_TECHNICAL_CUE_DETECTION,
        modelContext.VIDEO_SEGMENT_DETECTION,
      ]);

      return url ? this.#jobsApiService.getFile(url) : of<JobItem[]>([]);
    }),
  );

  /** Result URL of the newest completed job, taking the types in preference order. */
  #latestResultUrl(jobs: jobData[], types: modelContext[]): string | undefined {
    for (const type of types) {
      const newest = jobs
        .filter((item) => item.type === type && item.status === 'completed')
        .sort((a, b) => (new Date(a.created_at) > new Date(b.created_at) ? -1 : 1))[0];

      if (newest?.result_urls?.[0]?.url) {
        return newest.result_urls[0].url;
      }
    }

    return undefined;
  }

  #audioSilenceDetectionItems$: Observable<JobItem[]> = this.#jobsItems$.pipe(
    switchMap((jobsResponse) => jobsResponse.jobs.filter((item) => item.type === modelContext.AUDIO_SILENCE_DETECTION)),
    switchMap((job) => this.#jobsApiService.getFile(job.result_urls?.[0]?.url)),
  );

  #adBreakJobsItems$: Observable<JobItem[]> = this.#jobsItems$.pipe(
    map((jobsResponse) => {
      if (!jobsResponse.jobs.length) {
        this.#setEmptySourceData();
        return [];
      }

      const adBreakJobs: jobData[] = jobsResponse.jobs.filter((item) => item.type === modelContext.AD_BREAKS);

      if (!adBreakJobs.length) {
        this.#setEmptySourceData();
        return [];
      }

      return adBreakJobs;
    }),
    map((adBreakJobs: jobData[]) =>
      adBreakJobs
        .filter((item) => item.status === 'completed')
        .sort((a, b) => (new Date(a.created_at) > new Date(b.created_at) ? -1 : 1)),
    ),
    filter(Boolean),
    switchMap((jobs) => {
      if (!jobs.length) {
        this.#setEmptySourceData();
        return EMPTY;
      }

      const job = jobs[jobs.length - 1];

      return this.#jobsApiService.getFile(job.result_urls?.[0]?.url).pipe(
        tap((items) => {
          // A completed analysis that found no break now carries an empty result rather than failing, so the
          // empty state has to come from the file as well as from the absence of a job — otherwise the dialog
          // renders a grid of empty tabs with nothing to select and Save permanently disabled (#18144).
          if (!items?.length) {
            this.#setEmptySourceData();
          }
        }),
      );
    }),
  );

  $elementAdBreakFormArrayObjects = castTo<AdBreakFormArrayObject[]>();
  $elementAdBreakFormGroupObjects = castTo<AdBreakFormGroupObject[]>();

  emptySourceData$ = new BehaviorSubject<boolean>(false);

  adBreakItems: VideoAnalysisResultsResponse[] = [];
  blackItems: VideoAnalysisResultsResponse[] = [];
  silenceItems: VideoAnalysisResultsResponse[] = [];

  applyBlackSilenceFilter = false;

  optionsPerBreak!: number;
  allowInvalidBreakpoints = false;
  blackSilenceOnly = false;
  isLoading$ = new BehaviorSubject<boolean>(false);
  isSaving$ = new BehaviorSubject<boolean>(false);

  platformSpecs: AdBreakPlatformSpec[] = [];
  breakPointsScheme: SelectOption<string>[] = [];
  noPlatformSpecs$ = new BehaviorSubject<boolean>(false);
  /** Kept apart from `noPlatformSpecs$`: VIDA ships four global specs, so an empty list means the call failed. */
  specsFailed$ = new BehaviorSubject<boolean>(false);

  // Forms
  breakPointsSchemeForm = new FormGroup({
    breakPointsScheme: new FormControl<string | null>(null),
  });
  selectedBreaksForm = new FormArray<FormGroup<AdBreakFormGroup>>([]);

  highlightedHint: Record<number, AdBreakFormArrayObject['hint']> = {};

  breaksCount!: number;
  firstBreakAfterSec!: number;
  gapBetweenBreaksSec!: number;
  beforeFinalCreditsSec!: number;
  finalCreditsStart!: number;

  currentAssetUuid!: string;
  currentAssetOffset!: string;
  durationSec!: number;

  selectedPlaceholderTabIndex = 0;

  setPlayerService(playerService: PlayerService) {
    this.#playerService = playerService;
  }

  get selectedBreaksFormArray() {
    return this.selectedBreaksForm as FormArray;
  }

  trackItemBy = (index: number) => index;

  init() {
    // `destroy()` completes it, and this service is root-provided, so each open needs a fresh one
    // or the previous scheme listener is never torn down.
    this.#destroyed$ = new Subject<void>();
    this.selectedBreaksForm.clear();
    this.isLoading$.next(true);
    this.emptySourceData$.next(false);
    this.noPlatformSpecs$.next(false);
    this.specsFailed$.next(false);

    this.optionsPerBreak = this.DEFAULT_OPTIONS_PER_BREAK;
    this.allowInvalidBreakpoints = false;

    this.#platformSpecService
      .getAll()
      .pipe(
        catchError(() => {
          this.specsFailed$.next(true);

          return of<AdBreakPlatformSpec[]>([]);
        }),
        // The interceptor answers some failures with EMPTY rather than an error, and the dialog wrapper
        // hides its own close button while loading — without this the reviewer is stuck on a spinner.
        defaultIfEmpty<AdBreakPlatformSpec[], AdBreakPlatformSpec[]>([]),
        take(1),
        takeUntil(this.#destroyed$),
      )
      .subscribe((specs) => {
        this.platformSpecs = specs;
        this.breakPointsScheme = specs.map((spec) => ({ key: spec.uuid, label: spec.name }));

        if (!specs.length) {
          this.noPlatformSpecs$.next(!this.specsFailed$.value);
          this.isLoading$.next(false);

          return;
        }

        this.#applyPlatformSpec(specs[0]);
        this.breakPointsSchemeForm.controls.breakPointsScheme.setValue(specs[0].uuid, { emitEvent: false });
        this.#loadSourceData();
      });
  }

  #loadSourceData() {
    this.#audioSilenceDetectionItems$.pipe(take(1), takeUntil(this.#destroyed$)).subscribe((items) => {
      this.silenceItems = items.sort((a, b) => a.start - b.start);
    });

    // Joined, not raced: the first highlight pass needs `finalCreditsStart`, which arrives from a second
    // request. Independently subscribed, tab 1 was bounded by the asset duration instead of the credits.
    forkJoin({
      technicalCues: this.#technicalCueItems$.pipe(take(1), defaultIfEmpty<JobItem[], JobItem[]>([])),
      adBreaks: this.#adBreakJobsItems$.pipe(take(1), defaultIfEmpty<JobItem[], JobItem[]>([])),
    })
      .pipe(
        takeUntil(this.#destroyed$),
        finalize(() => this.isLoading$.next(false)),
      )
      .subscribe(({ technicalCues, adBreaks }) => {
        const endCreditsItems = technicalCues.filter((item) =>
          item.data?.content.toString().toLowerCase().includes('credits'),
        );
        const blackDetectionItems = technicalCues.filter((item) =>
          item.data?.content.toString().toLowerCase().includes('black'),
        );

        this.blackItems = blackDetectionItems.sort((a, b) => a.start - b.start);
        // Use last credits item if available, otherwise use duration as fallback
        this.finalCreditsStart =
          endCreditsItems.length > 0 ? endCreditsItems[endCreditsItems.length - 1].start : this.durationSec * 1000;

        this.adBreakItems = adBreaks.sort((a, b) => a.start - b.start);

        for (let i = 0; i < this.breaksCount; i++) {
          this.selectedBreaksForm.push(this.#adBreakFormGroup(null, this.adBreakItems));
        }

        const firstPlaceholderId = 0;
        this.#highlightSuggestions(firstPlaceholderId);
      });
  }

  destroy() {
    if (this.#markerTrackService.hasTrack(this.markerTrackData.id)) {
      this.#markerTrackService.removeMarkerTrack(this.markerTrackData.id);
      this.markerTrackData.values = [];
    }
    this.selectedBreaksForm.reset();
    this.breakPointsSchemeForm.reset();

    this.#destroyed$.next();
    this.#destroyed$.complete();
  }

  saveAdBreaks() {
    if (this.selectedBreaksForm.invalid) {
      return;
    }

    const adBreakLoggingTypeUuid = 'e8a7481a-729f-442d-b01d-1e512c19a0ef';
    const payload: LoggingInfo[] = this.$elementAdBreakFormGroupObjects(this.selectedBreaksForm.value).map((item) => ({
      asset: this.currentAssetUuid,
      logging_type: adBreakLoggingTypeUuid,
      responses: [],
      status: null,
      note: item.adBreaks.find((adBreak) => adBreak.selected)?.hint ?? this.N_A,
      offset: this.currentAssetOffset,
      timecode_in: item.selectedTimestamp,
      timecode_out: item.selectedTimestamp,
      uuid: uuidv4().toString(),
    }));

    this.isSaving$.next(true);
    this.#loggingApi
      .create(payload)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isSaving$.next(false);
          this.#toast.success({
            id: 'log',
            message: 'pages.edl.created',
          });
        },
        error: () => {
          this.isSaving$.next(false);
          this.#toast.error({
            id: 'log',
            message: 'common.global.fail',
          });
        },
      });
  }

  breakPointsSchemeFormListener() {
    this.breakPointsSchemeForm.controls.breakPointsScheme.valueChanges
      .pipe(takeUntil(this.#destroyed$))
      .subscribe((value) => {
        const spec = this.platformSpecs.find((item) => item.uuid === value);

        if (!spec) {
          return;
        }

        this.#applyPlatformSpec(spec);

        const lengthDifference = this.breaksCount - this.selectedBreaksForm.controls.length;
        if (this.breaksCount > this.selectedBreaksForm.controls.length) {
          for (let i = 0; i < lengthDifference; i++) {
            this.selectedBreaksForm.push(this.#adBreakFormGroup(null, this.adBreakItems));
          }
        } else {
          this.#removeLastSelectedBreaksFormElements(Math.abs(lengthDifference));
        }

        this.#revalidateAllHighlightedTimestamp();
        this.#revalidateAllSelectedTimestamp();
      });
  }

  changeOptionPerBreak(count: number) {
    if (
      this.optionsPerBreak + count < this.MIN_OPTIONS_PER_BREAK ||
      this.optionsPerBreak + count > this.MAX_OPTIONS_PER_BREAK
    ) {
      return;
    }

    this.optionsPerBreak += count;

    this.#revalidateAllHighlightedTimestamp();
    this.#revalidateAllSelectedTimestamp();
  }

  timecodeClick(breakObj: AdBreakFormArrayObject, placeholderIndex: number, adBreakIndex: number) {
    this.selectedBreaksFormArray
      .at(placeholderIndex)
      .get('selectedTimestamp')
      ?.patchValue(this.#timestampToTimecodeString(breakObj.timestamp));

    const currentSelectedIndex = this.$elementAdBreakFormArrayObjects(
      (this.selectedBreaksFormArray.at(placeholderIndex).get('adBreaks') as FormArray).value,
    ).findIndex((item) => item.selected);

    if (currentSelectedIndex !== -1) {
      (this.selectedBreaksFormArray.at(placeholderIndex).get('adBreaks') as FormArray)
        .at(currentSelectedIndex)
        .get('selected')
        ?.patchValue(false);
    }

    (this.selectedBreaksFormArray.at(placeholderIndex).get('adBreaks') as FormArray)
      .at(adBreakIndex)
      .get('selected')
      ?.patchValue(true);

    this.highlightedHint[placeholderIndex] = breakObj.hint;

    if (placeholderIndex < this.breaksCount - 1) {
      this.#highlightSuggestions(placeholderIndex + 1, breakObj.timestamp / 1000);
    }

    this.selectedBreaksFormArray.at(placeholderIndex).get('isValid')?.setValue(breakObj.highlighted);

    this.#goToTimecode(breakObj.timestamp);
    this.#addOrEditAdBreakMarker(placeholderIndex, breakObj);
  }

  tabClick(event: { index: number }) {
    this.#goToTimecode(this.selectedBreaksForm.at(event.index).get('selectedTimestamp')?.value as string);
  }

  selectedPlaceholderTab(index: number) {
    setTimeout(() => {
      const listElement = document.querySelector(
        '.ad-breaks-modal .mat-tab-body-active .ad-breaks-dialog-wrapper__bottom__selected-timestamp__list',
      ) as HTMLElement;
      const timestampChip = listElement?.querySelector('.highlighted') as HTMLElement;
      const chipWidthWithMargin = 160;
      if (listElement) {
        listElement.scroll({ top: 0, left: timestampChip?.offsetLeft - chipWidthWithMargin, behavior: 'smooth' });
      }
    }, 200);
  }

  allowInvalidBreakpointsChange($event: boolean) {
    switch ($event) {
      case true:
        this.selectedBreaksForm.controls.forEach((control) => {
          control.get('isValid')?.clearValidators();
          control.get('isValid')?.updateValueAndValidity();
        });
        break;
      case false:
        this.selectedBreaksForm.controls.forEach((control) => {
          control.get('isValid')?.setValidators([Validators.requiredTrue, Validators.nullValidator]);
          control.get('isValid')?.updateValueAndValidity();
        });
        break;
    }
  }

  blackSilenceOnlyFilter($event: boolean) {
    this.applyBlackSilenceFilter = $event;
  }

  #revalidateAllSelectedTimestamp() {
    this.selectedBreaksForm.controls.forEach((parentControl) => {
      (parentControl.get('adBreaks') as FormArray).controls.forEach((childControl) => {
        const highlighted = childControl.get('highlighted')?.value;
        const selected = childControl.get('selected')?.value;

        if (highlighted === true && selected === true) {
          parentControl.get('isValid')?.patchValue(true);
          parentControl.get('isValid')?.updateValueAndValidity();
        }
        if (highlighted === false && selected === true) {
          parentControl.get('isValid')?.patchValue(false);
          parentControl.get('isValid')?.updateValueAndValidity();
        }
      });
    });
  }

  #addOrEditAdBreakMarker(placeholderIndex: number, breakObj: AdBreakFormArrayObject) {
    const existingMarkerIndex = this.markerTrackData.values.findIndex((item) => item.id === placeholderIndex);
    const markerData: AdBreakMarkerValue = {
      id: placeholderIndex,
      tcInSeconds: breakObj.timestamp / 1000,
      tcOutSeconds: undefined,
      tooltip: breakObj.hint,
      color: breakObj.highlighted ? 'green' : 'orange',
    };

    if (existingMarkerIndex !== -1) {
      this.markerTrackData.values[existingMarkerIndex] = markerData;
    } else {
      this.markerTrackData.values.push(markerData);
    }

    if (this.#markerTrackService.hasTrack(this.markerTrackData.id)) {
      this.#markerTrackService.removeMarkerTrack(this.markerTrackData.id);
    }

    this.#markerTrackService.createFromConfig(this.markerTrackData);
  }

  #revalidateAllHighlightedTimestamp() {
    new Array(this.breaksCount).fill(0).forEach((item, index) => {
      if (index === 0) {
        this.#highlightSuggestions(index);
      } else {
        const prevBreakTimestamp = Timecode.fromTimecode(
          this.selectedBreaksFormArray.at(index - 1).get('selectedTimestamp')?.value,
        )?.countSeconds();
        this.#highlightSuggestions(index, prevBreakTimestamp);
      }
    });
  }

  #removeLastSelectedBreaksFormElements(elementsCount: number) {
    for (let i = 0; i < elementsCount; i++) {
      this.selectedBreaksForm.removeAt(this.selectedBreaksForm.controls.length - 1);
    }
  }

  #highlightSuggestions(placeholderId: number, nextHighlightCombineWithPrevSelection?: number) {
    const toHighlightedIds: number[] = [];

    // `!== undefined`, not truthiness: a previous break selected at 0 s is a selection, and treating it
    // as "none" silently swaps the minimum-gap rule for the first-break rule.
    const nextHighlightingStartAsSec =
      nextHighlightCombineWithPrevSelection !== undefined
        ? nextHighlightCombineWithPrevSelection + this.gapBetweenBreaksSec
        : this.firstBreakAfterSec;

    const programmeEndSec = this.finalCreditsStart ? this.finalCreditsStart / 1000 : this.durationSec;
    const endCredits = Math.max(programmeEndSec - this.beforeFinalCreditsSec, 0);

    const allAdBreaks = this.$elementAdBreakFormArrayObjects(
      this.selectedBreaksFormArray.at(placeholderId).get('adBreaks')?.value,
    );

    allAdBreaks.forEach((item, index) => {
      if (item.timestamp / 1000 >= nextHighlightingStartAsSec && item.timestamp / 1000 <= endCredits) {
        toHighlightedIds.push(index);
      }
    });

    this.#patchHighlightedFields(placeholderId, [...Array(allAdBreaks.length).keys()], false);
    this.#patchHighlightedFields(placeholderId, toHighlightedIds.slice(0, this.optionsPerBreak), true);
  }

  #patchHighlightedFields(placeholderId: number, toHighlightedIds: number[], state: boolean) {
    for (const id of toHighlightedIds) {
      (this.selectedBreaksFormArray.at(placeholderId).get('adBreaks') as FormArray)
        .at(id)
        .get('highlighted')
        ?.patchValue(state);
    }
  }

  #adBreakFormGroup(timestamp: string | null, adBreaks: VideoAnalysisResultsResponse[]) {
    return new FormGroup({
      selectedTimestamp: new FormControl<string | null>(timestamp, Validators.required),
      isValid: new FormControl<boolean | null>(null, [Validators.nullValidator, Validators.requiredTrue]),
      adBreaks: new FormArray<FormGroup<AdBreakFormArray>>(
        adBreaks.map(
          (adBreak) =>
            new FormGroup({
              timestamp: new FormControl<number | null>(adBreak.start),
              highlighted: new FormControl<boolean>(false),
              selected: new FormControl<boolean>(false),
              hint: new FormControl<string>((adBreak?.data?.content as string) ?? this.N_A),
            }) as FormGroup<AdBreakFormArray>,
        ),
      ),
    });
  }

  #goToTimecode(timestamp: number | string) {
    const timecode =
      typeof timestamp === 'string'
        ? Timecode.fromTimecode(timestamp as string)
        : this.#timestampToTimecode(Number(timestamp));
    if (!timecode) {
      return;
    }
    this.#playerService.goToTimecode(timecode);
  }

  #timestampToTimecodeString(timestamp: number) {
    return Timecode.fromSeconds(Number(timestamp / 1000))?.toString() ?? this.DEFAULT_TIMECODE;
  }

  #timestampToTimecode(timestamp: number) {
    return Timecode.fromSeconds(Number(timestamp / 1000));
  }

  #applyPlatformSpec(spec: AdBreakPlatformSpec) {
    this.breaksCount = spec.breaks_count;
    this.firstBreakAfterSec = spec.first_break_after_sec;
    this.gapBetweenBreaksSec = spec.min_gap_between_breaks_sec;
    this.beforeFinalCreditsSec = spec.min_before_final_credits_sec;
  }

  get firstBreakAfterMin() {
    return this.#toMinutes(this.firstBreakAfterSec);
  }

  get gapBetweenBreaksMin() {
    return this.#toMinutes(this.gapBetweenBreaksSec);
  }

  get beforeFinalCreditsMin() {
    return this.#toMinutes(this.beforeFinalCreditsSec);
  }

  #toMinutes(seconds: number) {
    return Math.round((seconds / 60) * 100) / 100;
  }

  #setEmptySourceData() {
    this.emptySourceData$.next(true);
    this.isLoading$.next(false);
  }
}
